import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './mobile-optimizations.css';
import './styles/tight-scrolling.css';
import './styles/arabic-rtl-overrides.css';
import * as serviceWorker from './serviceWorkerRegistration';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import { logger } from './utils/logger';
import { setupGlobalErrorHandlers } from './utils/errorHandler';
import { serviceWorkerManager } from './services/serviceWorkerManager';

// CRITICAL: Suppress IDB errors IMMEDIATELY before anything else
// These are harmless hot-reload errors that spam the console
if (typeof window !== 'undefined') {
  const isIDBError = (e: any) => {
    const msg = String(e?.message || e?.reason || e || '').toLowerCase();
    const name = String(e?.name || '').toLowerCase();
    return name === 'aborterror' || name === 'domexception' || 
           msg.includes('idbtransaction') || msg.includes('force close') ||
           msg.includes('connection is closing');
  };
  
  // Suppress IDB errors from console.error (prevents CRA overlay)
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    if (args.some(arg => isIDBError(arg))) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // Suppress before React's overlay can catch them
  window.addEventListener('error', (e) => {
    if (isIDBError(e.error || e.message)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }
  }, true);
  
  window.addEventListener('unhandledrejection', (e) => {
    if (isIDBError(e.reason)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);
}

// Setup additional error handlers
setupGlobalErrorHandlers();

// Set language and direction synchronously BEFORE React renders to prevent English flash
// This must happen before any React components render
// IMPORTANT: Always set dir attribute - default to LTR for English/Dutch, RTL only for Arabic
const savedLanguage = localStorage.getItem('preferredLanguage');
const html = document.documentElement;
if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'nl' || savedLanguage === 'ar')) {
  const isRTL = savedLanguage === 'ar';
  html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  html.setAttribute('lang', savedLanguage);
} else {
  // Default to LTR if no language is saved (English/Dutch are LTR) - default to Dutch
  html.setAttribute('dir', 'ltr');
  html.setAttribute('lang', 'nl');
}

// CHICKYTUTOR PATTERN: Preload critical routes immediately for instant navigation
// This ensures routes are ready before user navigates
const preloadCriticalRoutes = () => {
  if (typeof window === 'undefined') return;
  
  // CHICKYTUTOR PATTERN: Preload ALL frequently-used routes for instant navigation
  const criticalRoutes = [
    '/dashboard',
    '/tests',
    '/mock-exam',
    '/tests',
    '/ai-exam',
    '/subscription'
  ];
  
  criticalRoutes.forEach(route => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = route;
    link.as = 'document';
    document.head.appendChild(link);
  });
};

// Preload routes immediately (before React renders)
preloadCriticalRoutes();

// Unlock AI Exam for development: seed test history with 5 passing scores
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  try {
    const { unlockAIExamForTesting } = require('./services/aiExamUnlockService');
    unlockAIExamForTesting();
  } catch {
    // ignore
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <LanguageProvider>
      <ToastProvider>
        <App />
      </ToastProvider>
    </LanguageProvider>
  </React.StrictMode>
);

// Register enhanced service worker for offline-first architecture (Netflix/Duolingo style)
if (process.env.NODE_ENV === 'production') {
  serviceWorker.register({
    onSuccess: () => {
      logger.info('App is ready for offline use with enhanced caching');
    },
    onUpdate: () => {
      logger.info('New content available, please refresh');
    }
  });
  
  // Also register with enhanced service worker manager
  serviceWorkerManager.register().then(registered => {
    if (registered) {
      logger.info('Enhanced Service Worker active - offline-first architecture enabled');
    }
  });
} else {
  // Unregister service worker in development to prevent caching issues
  serviceWorker.unregister();
  serviceWorkerManager.unregister();
}

// Register mobile features
serviceWorker.registerInstallPrompt();
serviceWorker.registerOfflineDetection();

// Note: Global error handlers already set up at top of file

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
